import React from "react";
import { ArrowPathIcon, CheckIcon, ExclamationTriangleIcon, MinusIcon, PauseIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { formatToastElapsedTime } from "../../utils/toastProgress.js";
import { joinClasses } from "./shared.js";

const STATUS_LABELS = {
  pending: "À venir",
  running: "En cours",
  paused: "En attente de votre confirmation",
  completed: "Terminée",
  skipped: "Non nécessaire",
  warning: "À vérifier",
  failed: "Interrompue",
};

export default function ToastOperationProgress({ progress }) {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    setNow(Date.now());
    if (progress.pausedAt != null) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [progress.startedAt, progress.pausedAt]);

  if (progress.pausedAt == null && now - progress.startedAt < 1000) return null;
  const activeStep = progress.steps.find((step) => ["running", "paused"].includes(step.status));

  return (
    <div className="mx-5 mb-3 border-t border-black/8 pt-2" data-toast-operation-progress="true">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-[11px] text-floating-muted">
        <span className="font-semibold">Étapes du traitement</span>
        <span className="whitespace-nowrap tabular-nums" aria-live="off" data-toast-elapsed="true">
          Temps écoulé : {formatToastElapsedTime(progress.startedAt + (progress.pausedDurationMs || 0), progress.pausedAt ?? now)}
        </span>
      </div>
      <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {activeStep ? `${activeStep.label} : ${STATUS_LABELS[activeStep.status].toLowerCase()}.` : "Finalisation du traitement."}
      </p>
      <ol className="m-0 grid list-none gap-1.5 p-0" aria-label="Étapes du traitement">
        {progress.steps.map((step, index) => {
          const Icon = step.status === "completed" ? CheckIcon
            : step.status === "running" ? ArrowPathIcon
              : step.status === "paused" ? PauseIcon
              : step.status === "warning" ? ExclamationTriangleIcon
                : step.status === "failed" ? XMarkIcon
                  : step.status === "skipped" ? MinusIcon : null;
          const isActive = step.status === "running";
          const isPaused = step.status === "paused";
          return (
            <li key={step.key} className="grid min-w-0 grid-cols-[1.25rem_minmax(0,1fr)_auto] items-center gap-x-2 gap-y-0.5" aria-current={isActive || isPaused ? "step" : undefined} data-toast-step={step.key} data-toast-step-status={step.status}>
              <span aria-hidden="true" className={joinClasses([
                "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-[4px] border text-[10px] font-bold",
                step.status === "completed" ? "border-accent bg-accent text-white"
                  : isActive ? "border-accent bg-accent-soft text-accent"
                    : isPaused || step.status === "warning" ? "border-amber-300 bg-amber-50 text-amber-700"
                      : step.status === "failed" ? "border-red-300 bg-red-50 text-red-700"
                        : "border-slate-200 bg-slate-50 text-slate-400",
              ])}>
                {Icon ? <Icon className={joinClasses(["h-3.5 w-3.5", isActive ? "motion-safe:animate-spin motion-reduce:animate-none" : ""])} /> : index + 1}
              </span>
              <span className={joinClasses(["min-w-0 text-[12px] leading-4", isPaused ? "col-span-2 font-semibold text-amber-800" : isActive ? "font-semibold text-floating-ink-strong" : "text-floating-muted"])}>
                {step.label}
              </span>
              <span className={joinClasses(["text-[10px]", isPaused ? "col-span-2 col-start-2 text-amber-800" : isActive ? "text-accent" : "text-floating-muted"])}>
                {STATUS_LABELS[step.status] || STATUS_LABELS.pending}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
